/**
 * Calculadora de frete por CEP - Active Life
 * -------------------------------------------------------------------------
 * Usado pelo snippet "calculadora-frete.liquid" em dois lugares:
 *
 *   carrinho  (gaveta e página /cart) -> cota o carrinho inteiro pela API do
 *             carrinho da loja. Traz preço E prazo de cada opção.
 *   produto   (página do produto) -> cota só o produto aberto, na variante e
 *             quantidade escolhidas, num carrinho à parte da API da vitrine.
 *             O carrinho do cliente não é tocado e nenhum pixel de marketing
 *             é disparado. Essa API não informa prazo, só preço.
 *
 * Os preços vêm da própria Shopify (Frenet + regras de frete grátis do admin),
 * os mesmos que o cliente vê no checkout. Nada de preço é calculado aqui.
 *
 * O CEP digitado fica guardado no navegador do cliente e é reaproveitado nas
 * outras páginas: digitou no produto, o carrinho já abre com o frete calculado.
 */
(() => {
  if (customElements.get('calculadora-frete')) return;

  // ------------------------- CONFIGURACAO -------------------------
  // Valor mínimo (em reais) para frete grátis em cada região. Precisa ser IGUAL
  // ao "Perfil geral" em Configurações > Frete e entrega. É usado pela barra
  // "Compre mais R$ X para ganhar frete grátis!" do carrinho, que só aparece
  // depois que o cliente informa o CEP. Os preços da calculadora vêm da Shopify
  // e não dependem destes números.
  const FRETE_GRATIS_A_PARTIR_DE = {
    sudeste: 200,
    sul_centro_oeste: 299,
    norte_nordeste: 399,
  };
  // Versão da API da vitrine usada na página do produto. A Shopify mantém cada
  // versão por 12 meses e depois responde com a mais antiga ainda ativa.
  const VERSAO_API = '2026-07';
  // ---------------------- FIM DA CONFIGURACAO ----------------------

  // Faixas de CEP dos Correios. A Shopify só devolve frete com o estado (UF)
  // junto, e é o estado que decide a região de frete grátis.
  const FAIXAS_CEP = [
    [1000, 19999, 'SP'], [20000, 28999, 'RJ'], [29000, 29999, 'ES'], [30000, 39999, 'MG'],
    [40000, 48999, 'BA'], [49000, 49999, 'SE'], [50000, 56999, 'PE'], [57000, 57999, 'AL'],
    [58000, 58999, 'PB'], [59000, 59999, 'RN'], [60000, 63999, 'CE'], [64000, 64999, 'PI'],
    [65000, 65999, 'MA'], [66000, 68899, 'PA'], [68900, 68999, 'AP'], [69000, 69299, 'AM'],
    [69300, 69399, 'RR'], [69400, 69899, 'AM'], [69900, 69999, 'AC'], [70000, 72799, 'DF'],
    [72800, 72999, 'GO'], [73000, 73699, 'DF'], [73700, 76799, 'GO'], [76800, 76999, 'RO'],
    [77000, 77999, 'TO'], [78000, 78899, 'MT'], [79000, 79999, 'MS'], [80000, 87999, 'PR'],
    [88000, 89999, 'SC'], [90000, 99999, 'RS'],
  ];

  const REGIAO_POR_UF = {
    SP: 'Sudeste', RJ: 'Sudeste', MG: 'Sudeste', ES: 'Sudeste',
    PR: 'Sul', SC: 'Sul', RS: 'Sul',
    DF: 'Centro-Oeste', GO: 'Centro-Oeste', MT: 'Centro-Oeste', MS: 'Centro-Oeste',
    AM: 'Norte', PA: 'Norte', AC: 'Norte', RO: 'Norte', RR: 'Norte', AP: 'Norte', TO: 'Norte',
  };

  const CHAVE_CEP = 'calculadora-frete:cep';
  const TEXTO_ERRO_API = 'Não foi possível calcular o frete agora. Os valores aparecem no checkout.';

  const somenteDigitos = (texto) => String(texto || '').replace(/\D/g, '').slice(0, 8);
  const formatarCep = (texto) => somenteDigitos(texto).replace(/^(\d{5})(\d)/, '$1-$2');
  const esperar = (ms) => new Promise((ok) => setTimeout(ok, ms));
  const dinheiro = (valor) => Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const escapar = (texto) => String(texto).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

  function ufDoCep(cep) {
    const numero = parseInt(somenteDigitos(cep).slice(0, 5), 10);
    const faixa = FAIXAS_CEP.find(([inicio, fim]) => numero >= inicio && numero <= fim);
    return faixa ? faixa[2] : null;
  }

  function regiaoDaUf(uf) {
    return REGIAO_POR_UF[uf] || 'Nordeste';
  }

  function freteGratisDaUf(uf) {
    const regiao = regiaoDaUf(uf);
    if (regiao === 'Sudeste') return FRETE_GRATIS_A_PARTIR_DE.sudeste;
    if (regiao === 'Sul' || regiao === 'Centro-Oeste') return FRETE_GRATIS_A_PARTIR_DE.sul_centro_oeste;
    return FRETE_GRATIS_A_PARTIR_DE.norte_nordeste;
  }

  // ---------------------------- CEP salvo ----------------------------
  // Guardado no navegador. Se o navegador bloquear, vale só enquanto a página
  // estiver aberta.
  let cepEmMemoria = '';
  try {
    cepEmMemoria = somenteDigitos(localStorage.getItem(CHAVE_CEP));
  } catch (e) {}

  function salvarCep(cep) {
    if (cep === cepEmMemoria) return;
    cepEmMemoria = cep;
    try {
      localStorage.setItem(CHAVE_CEP, cep);
    } catch (e) {}
    atualizarBarraFreteGratis();
  }

  // A barra "Compre mais R$ X para ganhar frete grátis!" é desenhada pelo
  // cart.js do tema, que pergunta aqui o valor da região do CEP salvo.
  window.CalculadoraFrete = {
    limiteFreteGratis() {
      const uf = cepEmMemoria.length === 8 && ufDoCep(cepEmMemoria);
      return uf ? freteGratisDaUf(uf) * 100 : null;
    },
  };

  function atualizarBarraFreteGratis() {
    customElements.whenDefined('cart-items').then(() => {
      document.querySelectorAll('cart-items').forEach((carrinho) => {
        if (typeof carrinho.updateShippingNotification === 'function') {
          carrinho.updateShippingNotification('cart-shipping-notification');
        }
      });
    });
  }

  if (cepEmMemoria) atualizarBarraFreteGratis();

  // ----------------------------- Cotações -----------------------------
  // Uma cotação por combinação de CEP + carrinho/produto. Várias calculadoras
  // na mesma página (ou a mesma recriada pelo tema) reaproveitam a resposta.
  const cotacoes = new Map();

  function cotar(chave, buscar) {
    if (!cotacoes.has(chave)) {
      const promessa = buscar();
      cotacoes.set(chave, promessa);
      promessa.catch(() => cotacoes.delete(chave));
    }
    return cotacoes.get(chave);
  }

  // Carrinho: API do carrinho da loja. A Shopify responde 202 enquanto a
  // transportadora calcula, e 200 com as tarifas quando termina.
  async function cotarCarrinho(cep, uf) {
    const endereco = new URLSearchParams({
      'shipping_address[zip]': formatarCep(cep),
      'shipping_address[country]': 'Brazil',
      'shipping_address[province]': uf,
    });
    const base = (window.routes && window.routes.cart_url) || '/cart';

    const preparo = await fetch(`${base}/prepare_shipping_rates.json?${endereco}`, { method: 'POST' });
    if (!preparo.ok) throw new Error(`prepare_shipping_rates ${preparo.status}`);

    for (let tentativa = 0; tentativa < 20; tentativa++) {
      const resposta = await fetch(`${base}/async_shipping_rates.json?${endereco}`);
      if (resposta.status === 200) {
        const dados = await resposta.json();
        if (dados && dados.shipping_rates) {
          return dados.shipping_rates.map((tarifa) => ({
            nome: tarifa.presentment_name || tarifa.name,
            preco: parseFloat(tarifa.price),
            dias: tarifa.delivery_days && tarifa.delivery_days.length ? tarifa.delivery_days : null,
          }));
        }
      } else if (resposta.status !== 202) {
        throw new Error(`async_shipping_rates ${resposta.status}`);
      }
      await esperar(700);
    }
    throw new Error('async_shipping_rates demorou demais');
  }

  // Produto: carrinho à parte na API da vitrine (sem token). As tarifas da
  // transportadora chegam numa segunda parte da resposta (@defer).
  const CONSULTA_PRODUTO = `mutation CalculadoraFrete($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        ... @defer {
          deliveryGroups(first: 10, withCarrierRates: true) {
            nodes { deliveryOptions { title estimatedCost { amount } } }
          }
        }
      }
      userErrors { message }
    }
  }`;

  async function cotarProduto(cep, uf, variante, quantidade) {
    const resposta = await fetch(`/api/${VERSAO_API}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'multipart/mixed; deferSpec=20220824, application/json',
      },
      body: JSON.stringify({
        query: CONSULTA_PRODUTO,
        variables: {
          input: {
            lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variante}`, quantity: quantidade }],
            delivery: {
              addresses: [{
                selected: true,
                address: { deliveryAddress: { zip: formatarCep(cep), countryCode: 'BR', provinceCode: uf } },
              }],
            },
          },
        },
      }),
    });
    if (!resposta.ok) throw new Error(`graphql ${resposta.status}`);

    // Resposta em várias partes separadas por "--graphql", cada uma com um JSON
    const texto = await resposta.text();
    const limite = ((resposta.headers.get('content-type') || '').match(/boundary="?([^";]+)"?/) || [])[1];
    const partes = [];
    (limite ? texto.split(`--${limite}`) : [texto]).forEach((bloco) => {
      const inicio = bloco.indexOf('{');
      if (inicio === -1) return;
      try {
        partes.push(JSON.parse(bloco.slice(inicio)));
      } catch (e) {}
    });

    const grupos = [];
    const juntarGrupos = (entregas) => {
      entregas.nodes.forEach((grupo) => {
        grupos.push(grupo.deliveryOptions.map((opcao) => ({
          nome: opcao.title,
          preco: parseFloat(opcao.estimatedCost.amount),
          dias: null,
        })));
      });
    };

    partes.forEach((parte) => {
      if (parte.errors && parte.errors.length) throw new Error(parte.errors[0].message);
      const criado = parte.data && parte.data.cartCreate;
      if (criado && criado.userErrors && criado.userErrors.length) throw new Error(criado.userErrors[0].message);
      if (criado && criado.cart && criado.cart.deliveryGroups) juntarGrupos(criado.cart.deliveryGroups);
      (parte.incremental || []).forEach((incremento) => {
        if (incremento.data && incremento.data.deliveryGroups) juntarGrupos(incremento.data.deliveryGroups);
      });
    });

    return combinarGrupos(grupos);
  }

  // Só acontece se um dia o produto estiver num perfil de frete próprio junto
  // com outro: a Shopify cota cada grupo separado. Soma serviço com serviço de
  // mesmo nome; grupo sem aquele serviço entra com a opção mais barata dele.
  function combinarGrupos(grupos) {
    if (grupos.length <= 1) return grupos[0] || [];
    if (grupos.some((grupo) => grupo.length === 0)) return [];

    const maior = grupos.reduce((a, b) => (b.length > a.length ? b : a));
    return maior.map((opcao) => ({
      nome: opcao.nome,
      dias: null,
      preco: grupos.reduce((soma, grupo) => {
        const mesmoNome = grupo.find((outra) => outra.nome === opcao.nome);
        return soma + (mesmoNome ? mesmoNome.preco : Math.min(...grupo.map((outra) => outra.preco)));
      }, 0),
    }));
  }

  // --------------------------- Componente ---------------------------
  // O tema recria o rodapé do carrinho a cada mudança. O último resultado e o
  // painel aberto/fechado ficam guardados aqui fora, para a calculadora nova
  // já nascer igual à anterior enquanto recalcula, sem a gaveta pular.
  const ultimoEstado = {};
  let painelCarrinhoAberto = false;

  const SELO_GRATIS = '<span class="calc-frete__gratis">Grátis</span>';

  class CalculadoraFreteElement extends HTMLElement {
    connectedCallback() {
      if (this.iniciado) return;
      this.iniciado = true;

      this.modo = this.dataset.modo === 'produto' ? 'produto' : 'carrinho';
      this.campo = this.querySelector('[data-cep]');
      this.botao = this.querySelector('[data-calcular]');
      this.saida = this.querySelector('[data-resultado]');

      // Só no carrinho: a linha "Frete ..... Calcular" que abre as opções
      this.resumo = this.querySelector('[data-resumo]');
      this.corpo = this.querySelector('[data-corpo]');
      if (this.resumo) {
        this.abrirPainel(painelCarrinhoAberto);
        this.resumo.addEventListener('click', () => {
          this.abrirPainel(this.corpo.hidden);
          if (!this.corpo.hidden && somenteDigitos(this.campo.value).length !== 8) this.campo.focus();
        });
        this.querySelector('[data-alterar]').addEventListener('click', () => {
          this.setAttribute('data-editando', '');
          this.campo.focus();
          this.campo.select();
        });
      }

      this.campo.value = formatarCep(cepEmMemoria);

      this.campo.addEventListener('input', () => {
        this.campo.value = formatarCep(this.campo.value);
        if (somenteDigitos(this.campo.value).length === 8) this.calcular();
      });

      // No carrinho o campo fica dentro do <form> do checkout: sem isto, Enter
      // mandaria o cliente direto para o pagamento.
      this.campo.addEventListener('keydown', (evento) => {
        if (evento.key !== 'Enter') return;
        evento.preventDefault();
        this.calcular();
      });

      this.botao.addEventListener('click', () => this.calcular());

      if (this.modo === 'produto') {
        // Troca de variante ou de quantidade: recalcula se já havia cotação
        let espera;
        this.aoMudarProduto = () => {
          clearTimeout(espera);
          espera = setTimeout(() => {
            if (this.chaveAtual) this.calcular();
          }, 500);
        };
        document.addEventListener('change', this.aoMudarProduto);
      }

      // Com CEP já salvo, calcula sozinho, mas só quando a calculadora aparece
      // na tela. A gaveta do carrinho existe (fechada) em todas as páginas, e
      // sem isto cada página visitada gastaria uma cotação na transportadora.
      if (cepEmMemoria.length === 8) {
        if (ultimoEstado[this.modo]) this.pintar(ultimoEstado[this.modo]);
        if ('IntersectionObserver' in window) {
          this.observador = new IntersectionObserver((entradas) => {
            if (!entradas.some((entrada) => entrada.isIntersecting)) return;
            this.observador.disconnect();
            this.calcular();
          });
          this.observador.observe(this);
        } else {
          this.calcular();
        }
      }
    }

    disconnectedCallback() {
      if (this.aoMudarProduto) document.removeEventListener('change', this.aoMudarProduto);
      if (this.observador) this.observador.disconnect();
    }

    produtoEscolhido() {
      // A barra fixa de compra tem um formulário próprio (…-product-buy-bar)
      // que vem antes no HTML e não tem seletor de quantidade: pula ele.
      // getAttribute('id') e não form.id: o formulário tem um campo chamado
      // "id", e form.id devolveria esse campo em vez do id do formulário.
      const secao = this.closest('.shopify-section') || document;
      const forms = Array.from(secao.querySelectorAll('form[data-type="add-to-cart-form"]'));
      const form = forms.find((f) => !(f.getAttribute('id') || '').endsWith('-product-buy-bar')) || forms[0];
      const idForm = form && form.getAttribute('id');
      const campoVariante = form && form.querySelector('[name="id"]');
      const campoQuantidade = form
        && (document.querySelector(`[name="quantity"][form="${idForm}"]`) || form.querySelector('[name="quantity"]'));

      return {
        variante: (campoVariante && campoVariante.value) || this.dataset.variante,
        quantidade: Math.max(1, parseInt(campoQuantidade && campoQuantidade.value, 10) || 1),
      };
    }

    async calcular() {
      const cep = somenteDigitos(this.campo.value);
      if (cep.length !== 8) return this.mostrarAviso('Digite os 8 números do CEP.');

      const uf = ufDoCep(cep);
      if (!uf) return this.mostrarAviso('CEP não encontrado. Confira os números.');

      salvarCep(cep);

      let chave;
      let buscar;
      if (this.modo === 'produto') {
        const { variante, quantidade } = this.produtoEscolhido();
        chave = `produto|${cep}|${variante}|${quantidade}`;
        buscar = () => cotarProduto(cep, uf, variante, quantidade);
      } else {
        chave = `carrinho|${cep}|${this.dataset.assinatura}`;
        buscar = () => cotarCarrinho(cep, uf);
      }

      if (chave === this.chaveAtual) return;
      this.chaveAtual = chave;
      this.classList.add('is-loading');
      this.botao.disabled = true;
      if (this.resumo) {
        this.querySelector('[data-resumo-titulo]').textContent = `Frete para ${formatarCep(cep)}`;
        this.querySelector('[data-resumo-valor]').textContent = 'Calculando…';
        this.querySelector('.calc-frete__acao').classList.remove('is-link');
      }

      try {
        const opcoes = await cotar(chave, buscar);
        if (chave !== this.chaveAtual) return;
        this.mostrarOpcoes(cep, opcoes);
      } catch (erro) {
        if (chave !== this.chaveAtual) return;
        console.warn('[calculadora-frete]', erro);
        this.mostrarAviso(TEXTO_ERRO_API);
      } finally {
        if (chave === this.chaveAtual) {
          this.classList.remove('is-loading');
          this.botao.disabled = false;
        }
      }
    }

    mostrarOpcoes(cep, opcoes) {
      if (!opcoes.length) {
        this.mostrarAviso('Não encontramos opções de entrega para esse CEP.');
        return;
      }

      const ordenadas = opcoes.slice().sort((a, b) => a.preco - b.preco);
      const itens = ordenadas
        .map((opcao) => {
          let prazo = '';
          if (opcao.dias) {
            const [minimo, maximo] = opcao.dias;
            const unidade = maximo === 1 ? 'dia útil' : 'dias úteis';
            prazo = minimo === maximo ? `até ${maximo} ${unidade}` : `${minimo} a ${maximo} ${unidade}`;
          }
          return `<li class="calc-frete__opcao">
            <span class="calc-frete__nome">${escapar(opcao.nome)}${prazo ? `<span class="calc-frete__prazo">${prazo}</span>` : ''}</span>
            <span class="calc-frete__preco">${opcao.preco === 0 ? SELO_GRATIS : dinheiro(opcao.preco)}</span>
          </li>`;
        })
        .join('');

      // Na linha do carrinho vai só a opção mais barata
      const maisBarata = ordenadas[0].preco;
      let valor = SELO_GRATIS;
      if (maisBarata > 0) valor = `${ordenadas.length > 1 ? 'a partir de ' : ''}${dinheiro(maisBarata)}`;

      this.pintar({
        lista: `<ul class="calc-frete__opcoes" role="list">${itens}</ul>`,
        titulo: `Frete para ${formatarCep(cep)}`,
        valor,
        link: false,
        comResultado: true,
      });
    }

    mostrarAviso(texto) {
      this.chaveAtual = null;
      this.classList.remove('is-loading');
      this.botao.disabled = false;
      this.pintar({
        lista: `<p class="calc-frete__aviso">${escapar(texto)}</p>`,
        titulo: 'Frete',
        valor: 'Calcular',
        link: true,
        comResultado: false,
      });
      if (this.resumo) this.abrirPainel(true);
    }

    pintar(estado) {
      this.saida.innerHTML = estado.lista;
      this.toggleAttribute('data-com-resultado', estado.comResultado);
      this.removeAttribute('data-editando');
      if (this.resumo) {
        this.querySelector('[data-resumo-titulo]').textContent = estado.titulo;
        this.querySelector('[data-resumo-valor]').innerHTML = estado.valor;
        this.querySelector('.calc-frete__acao').classList.toggle('is-link', estado.link);
      }
      ultimoEstado[this.modo] = estado;
    }

    abrirPainel(aberto) {
      painelCarrinhoAberto = aberto;
      this.corpo.hidden = !aberto;
      this.resumo.setAttribute('aria-expanded', String(aberto));
      this.toggleAttribute('data-aberto', aberto);
    }
  }

  customElements.define('calculadora-frete', CalculadoraFreteElement);
})();
